import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultLayout from './DefaultLayout';
import PlainLayout from './PlainLayout';
import TransparentLayout from './TransparentLayout';
import { useAppStore } from '../../stores/app';
import { useLogger } from '@ui/shared/components/Logger';

/**
 * Layout component that dynamically renders the appropriate layout based on the type prop
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render within the layout
 * @param {string} [props.type='default'] - Layout type to use ('default', 'plain', or 'transparent')
 * @param {boolean} [props.requireAuthentication=false] - Whether to initialize the AppStore on mount
 * @returns {React.ReactElement} The appropriate layout component
 */
const Layout = ({ children, type = 'default', requireAuthentication = false, validPhases=null }) => {
  const { initializeApp } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const logger = useLogger();
  const phase = useAppStore((state) => state.phase);

  // Initialize the AppStore if authentication is required
  useEffect(() => {
    if (requireAuthentication) {
      const initialize = async () => {
        // Pass the current route path to initializeApp
        await initializeApp({initialRoute: location.pathname});
        // now add metadata to the logger...
        const appState = useAppStore.getState();
        logger.setMetadata({
          installSid: appState.installSid,
          examSid: appState.examSid,
          sessionSid: appState.sessionSid
        });
          
      };
      initialize();
    }
  }, [initializeApp, requireAuthentication]);

  /*
  // ensure the route is valid for the phase...
  useEffect(() => {
    if(phase && validPhases && validPhases?.length){
      // ensure the route and the phase match, if not push...
      if(!validPhases.includes(phase)){
        // now based on the phase, redirect to the proper place...
        if(phase === "offboarding" ){
          navigate('/session/offboarding', { replace: true });
        }else{
          navigate('/session/onboarding', { replace: true });
        }
      }
    }
  }, [phase, validPhases, location]);
*/

  // Select the appropriate layout component based on the type prop
  switch (type.toLowerCase()) {
    case 'plain':
      return <PlainLayout>{children}</PlainLayout>;
    case 'transparent':
      return <TransparentLayout>{children}</TransparentLayout>;
    case 'default':
    default:
      return <DefaultLayout>{children}</DefaultLayout>;
  }
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['default', 'plain', 'transparent']),
  requireAuthentication: PropTypes.bool,

};

Layout.defaultProps = {
  type: 'default',
  requireAuthentication: false,
};

export default Layout;
